import React from 'react';
import { LOCALSTORAGE_KEYS } from '@constants';
import { Box, CircularProgress, SxProps } from '@mui/material';
import { accountSelectedSelector } from 'atoms';
import axios from 'axios';
import dayjs from 'dayjs';
import { useRecoilValue } from 'recoil';
import { Account } from 'types/Account';
import MySecurityDescriptionIcon from './MySecurityDescriptionIcon';
import { getProxyURL } from 'services/proxy';

const SERVER_URI = 'https://branding.segware.com/branding';

function Logo({ sx, sxCustomImage }: { sx?: SxProps; sxCustomImage?: SxProps }) {
  const { masterCompanyId, companyId } = useLogoInfo();
  const { src, loading } = useSrcLogo({
    masterCompanyId,
    companyId,
  });

  if (loading) {
    return (
      <Box sx={{ ...sx, mx: 'auto' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (src) {
    return <Box component="img" {...{ src }} alt="logo" sx={sxCustomImage || sx} />;
  }

  return <MySecurityDescriptionIcon {...{ sx }} />;
}

function useLogoInfo(): {
  masterCompanyId: number | null;
  companyId: number | null;
} {
  const accountSelected = useRecoilValue(accountSelectedSelector);

  const getId = React.useCallback(
    (accountKey: keyof Pick<Account, 'companyId' | 'masterCompanyId'>, localStorageKey: string) => {
      if (accountSelected) {
        return accountSelected[accountKey];
      }
      const id = localStorage.getItem(localStorageKey);
      return id ? Number(id) : null;
    },
    [accountSelected],
  );

  const companyId = React.useMemo(() => getId('companyId', LOCALSTORAGE_KEYS.COMPANY_ID), [getId]);

  const masterCompanyId = React.useMemo(
    () => getId('masterCompanyId', LOCALSTORAGE_KEYS.MASTER_COMPANY_ID),
    [getId],
  );

  React.useEffect(
    function setLocalStorage() {
      if (accountSelected) {
        localStorage.setItem(LOCALSTORAGE_KEYS.COMPANY_ID, String(accountSelected.companyId));
        localStorage.setItem(
          LOCALSTORAGE_KEYS.MASTER_COMPANY_ID,
          String(accountSelected.masterCompanyId),
        );
      } else {
        localStorage.removeItem(LOCALSTORAGE_KEYS.COMPANY_ID);
        localStorage.removeItem(LOCALSTORAGE_KEYS.MASTER_COMPANY_ID);
      }
    },
    [accountSelected],
  );

  return { companyId, masterCompanyId };
}

function useSrcLogo({
  masterCompanyId,
  companyId,
}: {
  masterCompanyId: number | null;
  companyId: number | null;
}) {
  const [src, setSrc] = React.useState<string>();
  const [loading, setLoading] = React.useState(true);
  const pathByCompany = React.useMemo(
    () =>
      getProxyURL(`${SERVER_URI}/${masterCompanyId}/${companyId}/logo.png?hash=${dayjs().unix()}`),
    [masterCompanyId, companyId],
  );
  const pathMasterCompany = React.useMemo(
    () => getProxyURL(`${SERVER_URI}/${masterCompanyId}/logo.png?hash=${dayjs().unix()}`),
    [masterCompanyId],
  );

  React.useEffect(() => {
    async function fetchLogos() {
      setLoading(true);
      try {
        await axios.get(pathByCompany);
        setSrc(pathByCompany);
      } catch (error) {
        try {
          await axios.get(pathMasterCompany);
          setSrc(pathMasterCompany);
        } catch (error2) {
          setSrc(undefined);
        }
      }
      setLoading(false);
    }

    fetchLogos();
  }, [pathByCompany, pathMasterCompany]);

  return { src, loading };
}

export default Logo;
